import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CellSelectionOptions, ColDef, ColGroupDef } from 'ag-grid-enterprise'
import { AccountMapGridRow, CompanyColumnInfo } from '../../utils/accountMapUtils'

const getPairValues = (data: AccountMapGridRow | undefined, companyId: string) => {
  const from = data?.companies?.[companyId]?.from ?? null
  const to = data?.companies?.[companyId]?.to ?? null
  return { from, to }
}

/** Normalize empty string / NaN to null and coerce string inputs to number (e.g. clipboard paste) */
const normalizeValue = (val: unknown): number | null => {
  if (val == null || val === '') return null
  const num = Number(val)
  return Number.isNaN(num) ? null : num
}

/** Check if a from/to pair is incomplete (one filled, the other empty) */
export const isIncompletePair = (data: AccountMapGridRow | undefined, companyId: string): boolean => {
  const { from, to } = getPairValues(data, companyId)
  return (from != null) !== (to != null)
}

/** Check if from > to when both are filled */
export const isFromGreaterThanTo = (data: AccountMapGridRow | undefined, companyId: string): boolean => {
  const { from, to } = getPairValues(data, companyId)
  return from != null && to != null && from > to
}

interface UseAccountMapColumnsParams {
  companies: CompanyColumnInfo[]
  editable: boolean
  getStatementRowName: (statementRowId: number) => string
}

export function useAccountMapColumns({ companies, editable, getStatementRowName }: UseAccountMapColumnsParams) {
  const { t } = useTranslation()

  const colDefs = useMemo<(ColDef<AccountMapGridRow> | ColGroupDef<AccountMapGridRow>)[]>(() => {
    const columns: (ColDef<AccountMapGridRow> | ColGroupDef<AccountMapGridRow>)[] = [
      {
        field: 'statementRowId',
        headerName: t('accountMapPage:statementRow', 'Statement Row'),
        width: 250,
        editable: false,
        rowGroup: true,
        hide: true,
        valueFormatter: params => {
          if (params.value == null) return ''
          return getStatementRowName(params.value)
        }
      }
    ]

    // Add column group for each company
    companies.forEach(({ companyId: cId, companyName }, index) => {
      const isOdd = index % 2 === 1
      const cellStyle = isOdd ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' } : undefined
      const headerClass = isOdd ? 'company-odd' : undefined
      columns.push({
        headerName: companyName,
        headerClass,
        children: (['from', 'to'] as const).map(field => ({
          field: `companies.${cId}.${field}` as keyof AccountMapGridRow,
          headerName: field === 'from' ? t('contextDrawer:start') : t('contextDrawer:end'),
          width: 100,
          editable,
          type: 'numeric' as const,
          filter: false,
          suppressHeaderMenuButton: true,
          cellStyle,
          headerClass,
          cellEditorParams: {
            decimals: 0,
            allowNegative: false
          },
          cellClassRules: {
            'cell-validation-error': (params: {
              data: AccountMapGridRow | undefined
              context?: { validationErrors?: boolean }
            }) =>
              isFromGreaterThanTo(params.data, cId) ||
              !!(params.context?.validationErrors && isIncompletePair(params.data, cId))
          },
          valueFormatter: (params: { value: unknown }) => (params.value != null ? String(params.value) : ''),
          valueGetter: (params: { data: AccountMapGridRow | undefined }) =>
            params.data?.companies?.[cId]?.[field] ?? null,
          valueSetter: (params: { data: AccountMapGridRow | undefined; newValue: unknown }) => {
            if (params.data) {
              const { data } = params
              if (!data.companies[cId]) {
                data.companies[cId] = { from: null, to: null }
              }
              data.companies[cId][field] = normalizeValue(params.newValue)
              return true
            }
            return false
          }
        }))
      })
    })

    return columns
  }, [t, editable, companies, getStatementRowName])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMovable: true,
      editable
    }),
    [editable]
  )

  const cellSelection = useMemo<CellSelectionOptions>(
    () => ({
      handle: {
        mode: 'fill',
        direction: 'x',
        setFillValue: params => {
          const colId = params.column.getColId()
          const isFromColumn = colId.endsWith('.from')
          const isToColumn = colId.endsWith('.to')

          if (!isFromColumn && !isToColumn) {
            return false // Use default behavior for other columns
          }

          const cellRanges = params.api.getCellRanges()
          const rangeColIds = cellRanges?.[0]?.columns.map(c => c.getColId()) ?? []
          const fromIndex = rangeColIds.findIndex(id => id.endsWith('.from'))
          const toIndex = rangeColIds.findIndex(id => id.endsWith('.to'))

          // Both from and to are in the selection — use the exact column
          // position in the range so multi-company selections get the correct
          // initialValue (findIndex('.from') only finds the first company's column)
          if (fromIndex !== -1 && toIndex !== -1) {
            const currentColId = params.column?.getColId()
            const selectedIdx = rangeColIds.findIndex(id => id === currentColId)
            const fallbackIdx = isFromColumn ? fromIndex : toIndex
            return params.initialValues[selectedIdx !== -1 ? selectedIdx : fallbackIdx] ?? params.initialValues[0]
          }

          // Single column selected
          return params.initialValues[0]
        }
      }
    }),
    []
  )

  return { colDefs, defaultColDef, cellSelection }
}
